/**
 *
 * Lightweight finite state machine helper for multi-step wizards/forms
 * @param config
 */
export const StateMachine = (config) => {
  const {
    steps = [],
    transitions = {},
    previousMap = {},
    validators = {},
    nextResolvers = {},
    previousResolvers = {},
  } = config;

  const isValidStep = (step) => step != null && Object.hasOwn(transitions, step);

  const getAllowed = (step) => {
    const t = transitions[step];
    if (Array.isArray(t)) return t;
    if (typeof t === 'string') return [t];
    return [];
  };

  const canTransition = (from, to) => getAllowed(from).includes(to);

  const validate = (step, context = {}) => {
    const validator = validators[step];
    return typeof validator === 'function' ? !!validator(context) : true;
  };

  const resolveNext = (currentStep, context = {}) => {
    if (!isValidStep(currentStep)) return null;

    const allowed = getAllowed(currentStep);
    if (allowed.length === 0) return null;

    if (!validate(currentStep, context)) return null;

    const resolver = nextResolvers[currentStep];
    if (typeof resolver === 'function') {
      const resolved = resolver(context);
      return canTransition(currentStep, resolved) ? resolved : null;
    }

    return allowed[0];
  };

  const resolvePrevious = (currentStep, context = {}) => {
    if (!isValidStep(currentStep)) return null;

    const resolver = previousResolvers[currentStep];
    const fallback = previousMap[currentStep] ?? null;

    if (typeof resolver === 'function') {
      return resolver(context, fallback);
    }

    return fallback;
  };

  return {
    isValidStep,
    canTransition,
    validate,
    resolveNext,
    resolvePrevious,
    getAllowedTransitions: getAllowed,
    isTerminal: (step) => getAllowed(step).length === 0,
    getStepIndex: (step) => steps.indexOf(step),
    isForward: (from, to) => {
      const fi = steps.indexOf(from);
      const ti = steps.indexOf(to);
      return fi !== -1 && ti !== -1 && ti > fi;
    },

    canGoNext: (step, ctx) => !!resolveNext(step, ctx),
    canGoPrevious: (step, ctx) => !!resolvePrevious(step, ctx),
  };
};
